import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function NotificationBell({ userId }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]               = useState(0);
  const [open, setOpen]                   = useState(false);
  const ref                               = useRef(null);
  const navigate                          = useNavigate();

  async function load() {
    try {
      const res  = await fetch('/api/notifications', { credentials:'include' });
      const data = await res.json();
      if (data.success) { setNotifications(data.data); setUnread(data.unread); }
    } catch {}
  }

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function markAllRead() {
    await fetch('/api/notifications/read-all', { method:'PUT', credentials:'include' });
    setUnread(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read:true })));
  }

  async function handleClick(n) {
    if (!n.is_read) {
      await fetch(`/api/notifications/${n.id}/read`, { method:'PUT', credentials:'include' });
      setNotifications(prev => prev.map(x => x.id===n.id ? {...x,is_read:true} : x));
      setUnread(prev => Math.max(0, prev-1));
    }
    if (n.type === 'change_request') {
      navigate('/change-approvals');
      setOpen(false);
      return;
    }
    if (n.type === 'guideline_change_request' && n.campaign_id) {
      // This lives in the campaign's Edit page (Rejection guidelines card),
      // not the campaign overview — the generic campaign_id fallback below
      // was landing on the overview page, which has no Approve/Reject UI
      // for this at all.
      navigate(`/campaigns/${n.campaign_id}/edit?section=guidelines`);
      setOpen(false);
      return;
    }
    if (n.campaign_id) {
      // Land on the campaign itself, not the campaign list. An address-review
      // alert opens straight on the Outreach tab where the row + "Address to
      // review" pill live.
      navigate(`/campaigns-v32/${n.campaign_id}`);
      setOpen(false);
    }
  }

  function timeAgo(date) {
    const diff = (Date.now() - new Date(date)) / 1000;
    if (diff < 60)    return 'just now';
    if (diff < 3600)  return Math.round(diff/60) + 'm ago';
    if (diff < 86400) return Math.round(diff/3600) + 'h ago';
    return Math.round(diff/86400) + 'd ago';
  }

  return (
    <div ref={ref} className="notif-wrap">
      <button
        className="notif-btn"
        onClick={() => setOpen(o=>!o)}
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="notif-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span className="notif-badge" aria-hidden="true">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown" role="dialog" aria-label="Notifications">
          <div className="notif-header">
            <span className="notif-header-title">Notifications</span>
            {unread > 0 && (
              <button className="notif-mark-read" onClick={markAllRead}>Mark all read</button>
            )}
          </div>
          <div className="notif-list" role="list">
            {notifications.length === 0 ? (
              <div className="notif-empty">No notifications yet</div>
            ) : notifications.map(n => (
              <div
                key={n.id}
                role="listitem"
                className={`notif-item${n.is_read ? '' : ' unread'}${n.campaign_id ? ' clickable' : ''}`}
                onClick={() => handleClick(n)}
              >
                <div className="notif-item-row">
                  <div className={`notif-title${n.is_read ? '' : ' unread'}`}>{n.title}</div>
                  <div className="notif-time">{timeAgo(n.created_at)}</div>
                </div>
                {n.body && <div className="notif-body">{n.body}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
